import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User } from '../../services/authService';
import { MusicSheetService, MusicSheet, SheetSummary, GenerateOptions } from '../../services/musicSheetService';
import PageHeader from '../common/PageHeader';
import './MusicSheets.css';
import abcjs from 'abcjs';

interface MusicSheetsProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type ViewMode = 'current' | 'past-list' | 'past-sheet';

const MusicSheets: React.FC<MusicSheetsProps> = ({
  user,
  onClose,
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const [currentSheet, setCurrentSheet] = useState<MusicSheet | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('current');
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [pastSheets, setPastSheets] = useState<SheetSummary[]>([]);
  const [selectedPastSheet, setSelectedPastSheet] = useState<MusicSheet | null>(null);

  // Generation form state
  const [textPrompt, setTextPrompt] = useState('');
  const [genre, setGenre] = useState('');
  const [tempo, setTempo] = useState('');
  const [key, setKey] = useState('');
  const [mood, setMood] = useState('');

  const menuRef = useRef<HTMLDivElement>(null);
  const abcContainerRef = useRef<HTMLDivElement>(null);
  const synthControlRef = useRef<any>(null);

  // Load latest sheet on mount
  useEffect(() => {
    loadLatestSheet();
  }, []);

  const loadLatestSheet = async () => {
    try {
      const result = await MusicSheetService.getMusicSheets(1);
      if (result && result.sheets && result.sheets.length > 0) {
        const latestSheet = await MusicSheetService.getSheetById(result.sheets[0].sheet_id);
        setCurrentSheet(latestSheet);
      }
    } catch (err) {
      console.error('Error loading latest sheet:', err);
      // It's okay if there are no sheets yet
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);

  // Helper function to fix common ABC notation syntax errors
  const fixABCNotation = (abcNotation: string): string => {
    let fixed = abcNotation;
    
    // Fix incorrect repeat endings: :|1 should be :|
    // This is the most common error - :|1 is invalid ABC syntax
    fixed = fixed.replace(/:\|1/g, ':|');
    
    // Fix |1 at end of measure (should be just |)
    // Only replace standalone |1 at end of line, not |1 followed by other content
    fixed = fixed.replace(/\|1(\s*)$/gm, '|$1');
    
    // Ensure proper line breaks (normalize to \n)
    fixed = fixed.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    
    // Remove trailing whitespace from lines
    fixed = fixed.split('\n').map(line => line.trimEnd()).join('\n');
    
    return fixed;
  };

  // Render ABC notation when sheet changes
  useEffect(() => {
    const displayedSheet = viewMode === 'past-sheet' ? selectedPastSheet : currentSheet;
    if (displayedSheet && abcContainerRef.current) {
      renderABCNotation(displayedSheet.abc_notation);
    }
  }, [currentSheet, selectedPastSheet, viewMode]);

  const renderABCNotation = (abcNotation: string) => {
    if (!abcContainerRef.current) return;

    try {
      // Clear previous content
      abcContainerRef.current.innerHTML = '';

      // Fix common syntax errors
      const fixedNotation = fixABCNotation(abcNotation);

      // Render the ABC notation
      const renderResult = abcjs.renderAbc(abcContainerRef.current, fixedNotation, {
        responsive: 'resize',
        scale: 1.0
      });

      // Check if rendering was successful
      if (!renderResult || !renderResult[0]) {
        console.error('ABC notation rendered but no output generated');
        setError('Failed to render music notation - no output generated. Please check ABC syntax.');
        return;
      }

      // Check for warnings in the render result
      if (renderResult[0].warnings && renderResult[0].warnings.length > 0) {
        console.warn('ABC notation warnings:', renderResult[0].warnings);
      }

      console.log('🎵 ABC notation rendered successfully');
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error rendering ABC notation:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to render music notation';
      setError(`ABC notation error: ${errorMessage}. Please check the ABC syntax.`);
    }
  };

  const handleOpenGenerateModal = () => {
    setShowMenu(false);
    setShowGenerateModal(true);
    // Reset form
    setTextPrompt('');
    setGenre('');
    setTempo('');
    setKey('');
    setMood('');
  };

  const handleGenerate = async () => {
    if (!textPrompt && !genre && !tempo && !key && !mood) {
      setError('Please provide at least one input');
      return;
    }

    try {
      setShowGenerateModal(false);
      setIsGenerating(true);
      setError(null);

      const options: GenerateOptions = {};
      if (textPrompt) options.textPrompt = textPrompt;
      if (genre) options.genre = genre;
      if (tempo) options.tempo = tempo;
      if (key) options.key = key;
      if (mood) options.mood = mood;

      const newSheet = await MusicSheetService.generateMusicSheet(options);
      
      setCurrentSheet(newSheet);
      setIsGenerating(false);
      setViewMode('current');
    } catch (err) {
      console.error('Error generating music sheet:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate music sheet. Please try again.');
      setIsGenerating(false);
    }
  };

  const handleOpenPastSheets = async () => {
    setShowMenu(false);
    try {
      const result = await MusicSheetService.getMusicSheets(50);
      setPastSheets(result.sheets);
      setViewMode('past-list');
    } catch (err) {
      console.error('Error loading past sheets:', err);
      setError('Failed to load past sheets');
    }
  };

  const handleSelectPastSheet = async (sheetId: string) => {
    try {
      const sheet = await MusicSheetService.getSheetById(sheetId);
      setSelectedPastSheet(sheet);
      setViewMode('past-sheet');
    } catch (err) {
      console.error('Error loading sheet:', err);
      setError('Failed to load sheet');
    }
  };

  const handleBackToCurrentSheet = () => {
    setViewMode('current');
    setSelectedPastSheet(null);
  };

  const handlePlayPause = async () => {
    const displayedSheet = viewMode === 'past-sheet' ? selectedPastSheet : currentSheet;
    if (!displayedSheet) return;

    try {
      if (isPlaying) {
        // Stop playback
        if (synthControlRef.current) {
          synthControlRef.current.pause();
        }
        setIsPlaying(false);
      } else {
        // Start playback
        if (abcjs.synth.supportsAudio()) {
          // Fix ABC notation before rendering for playback
          const fixedNotation = fixABCNotation(displayedSheet.abc_notation);
          
          const renderResult = abcjs.renderAbc('*', fixedNotation);
          if (!renderResult || !renderResult[0]) {
            throw new Error('Failed to parse ABC notation for playback');
          }
          
          const visualObj = renderResult[0];
          
          // Clean up previous synth if exists
          if (synthControlRef.current) {
            try {
              synthControlRef.current.pause();
            } catch (e) {
              // Ignore errors when cleaning up
            }
          }
          
          synthControlRef.current = new abcjs.synth.CreateSynth();
          await synthControlRef.current.init({
            visualObj: visualObj
          });

          await synthControlRef.current.prime();
          synthControlRef.current.start();
          setIsPlaying(true);

          // Listen for when playback ends
          synthControlRef.current.onEnded = () => {
            setIsPlaying(false);
          };
        } else {
          setError('Audio playback is not supported in your browser');
        }
      }
    } catch (err) {
      console.error('Error playing music:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to play music';
      setError(`Playback error: ${errorMessage}. Please check ABC notation syntax.`);
      setIsPlaying(false);
    }
  };

  const displayedSheet = viewMode === 'past-sheet' ? selectedPastSheet : currentSheet;
  const showBackButton = viewMode === 'past-sheet';

  return (
    <div className="music-sheets-container">
      {/* Past Sheets List View */}
      {viewMode === 'past-list' && (
        <div className="music-sheets-past-list">
          <PageHeader
            onToggleSidebar={onToggleSidebar}
            leftActions={
              <button 
                className="page-header-icon-btn"
                onClick={handleBackToCurrentSheet}
                aria-label="Back"
              >
                ←
              </button>
            }
            centerContent={
              <h1 className="page-header-center-title">Past Music Sheets</h1>
            }
          />
          
          <div className="music-sheets-past-content">
            {pastSheets.length === 0 ? (
              <div className="music-sheets-empty">
                <p>No saved music sheets yet.</p>
                <button 
                  className="music-sheets-btn-primary"
                  onClick={handleOpenGenerateModal}
                >
                  Generate Your First Sheet
                </button>
              </div>
            ) : (
              <div className="music-sheets-list">
                {pastSheets.map((sheet) => (
                  <button
                    key={sheet.sheet_id}
                    className="music-sheets-list-item"
                    onClick={() => handleSelectPastSheet(sheet.sheet_id)}
                  >
                    <div className="music-sheets-list-item-content">
                      <h3 className="music-sheets-list-item-title">{sheet.sheet_name}</h3>
                      {sheet.description && (
                        <p className="music-sheets-list-item-desc">{sheet.description}</p>
                      )}
                      <span className="music-sheets-list-item-date">
                        {new Date(sheet.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Sheet View */}
      {viewMode !== 'past-list' && (
        <>
          {/* Page Header */}
          <PageHeader
            onToggleSidebar={onToggleSidebar}
            leftActions={
              showBackButton ? (
                <button 
                  className="page-header-icon-btn"
                  onClick={handleBackToCurrentSheet}
                  aria-label="Back to current"
                >
                  ←
                </button>
              ) : undefined
            }
            centerContent={
              <h1 className="page-header-center-title">
                {viewMode === 'past-sheet' ? 'Past Sheet' : 'Music Sheets'}
              </h1>
            }
            rightActions={
              <div className="music-sheets-menu-container" ref={menuRef}>
                <button 
                  className="page-header-icon-btn"
                  onClick={() => setShowMenu(!showMenu)}
                  disabled={isGenerating}
                >
                  ⋮
                </button>
                {showMenu && (
                  <div className="music-sheets-menu-dropdown">
                    <button
                      className="music-sheets-menu-item"
                      onClick={() => {
                        setShowMenu(false);
                        navigate('/music-sheets/editor/new');
                      }}
                    >
                      ✏️ Create New Song
                    </button>
                    <button
                      className="music-sheets-menu-item"
                      onClick={handleOpenGenerateModal}
                    >
                      ✨ Generate New Music
                    </button>
                    <button
                      className="music-sheets-menu-item"
                      onClick={handleOpenPastSheets}
                    >
                      📚 Past Sheets
                    </button>
                  </div>
                )}
              </div>
            }
          />

          {/* Main Content */}
          <div className="music-sheets-main-content">
            {/* Loading State */}
            {isGenerating && (
              <div className="music-sheets-loading">
                <div className="music-sheets-spinner"></div>
                <p className="music-sheets-loading-text">
                  Composing your music...
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && !isGenerating && (
              <div className="music-sheets-error">
                <p>{error}</p>
              </div>
            )}

            {/* Sheet Content */}
            {displayedSheet && !isGenerating && (
              <div className="music-sheets-display">
                <div className="music-sheets-header">
                  <h2 className="music-sheets-title">{displayedSheet.sheet_name}</h2>
                  {displayedSheet.description && (
                    <p className="music-sheets-description">{displayedSheet.description}</p>
                  )}
                  {displayedSheet.metadata && Object.keys(displayedSheet.metadata).length > 0 && (
                    <div className="music-sheets-metadata">
                      {displayedSheet.metadata.genre && (
                        <span className="music-sheets-meta-tag">Genre: {displayedSheet.metadata.genre}</span>
                      )}
                      {displayedSheet.metadata.tempo && (
                        <span className="music-sheets-meta-tag">Tempo: {displayedSheet.metadata.tempo} BPM</span>
                      )}
                      {displayedSheet.metadata.key && (
                        <span className="music-sheets-meta-tag">Key: {displayedSheet.metadata.key}</span>
                      )}
                      {displayedSheet.metadata.mood && (
                        <span className="music-sheets-meta-tag">Mood: {displayedSheet.metadata.mood}</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Music Notation Display */}
                <div className="music-sheets-notation" ref={abcContainerRef}></div>

                {/* Playback Controls */}
                <div className="music-sheets-controls">
                  <button
                    className={`music-sheets-play-btn ${isPlaying ? 'playing' : ''}`}
                    onClick={handlePlayPause}
                  >
                    {isPlaying ? '⏸ Pause' : '▶ Play'}
                  </button>
                  {displayedSheet.sheet_id && (
                    <button
                      className="music-sheets-edit-btn"
                      onClick={() => navigate(`/music-sheets/editor/${displayedSheet.sheet_id}`)}
                    >
                      ✏️ Edit
                    </button>
                  )}
                </div>

                {/* ABC Notation Source */}
                <details className="music-sheets-abc-source">
                  <summary>View ABC Notation Source</summary>
                  <pre className="music-sheets-abc-code">{displayedSheet.abc_notation}</pre>
                </details>
              </div>
            )}

            {/* Welcome Message */}
            {!displayedSheet && !isGenerating && !error && (
              <div className="music-sheets-welcome">
                <h2>Welcome to Music Sheets</h2>
                <p>Generate AI-powered music compositions in ABC notation format.</p>
                <button 
                  className="music-sheets-btn-primary"
                  onClick={handleOpenGenerateModal}
                >
                  Generate Your First Sheet
                </button>
                <div className="music-sheets-features">
                  <div className="music-sheets-feature">
                    <span className="music-sheets-feature-icon">🎵</span>
                    <span>AI-Generated Melodies</span>
                  </div>
                  <div className="music-sheets-feature">
                    <span className="music-sheets-feature-icon">🎹</span>
                    <span>Interactive Playback</span>
                  </div>
                  <div className="music-sheets-feature">
                    <span className="music-sheets-feature-icon">📝</span>
                    <span>ABC Notation Format</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Generate Modal */}
      {showGenerateModal && (
        <>
          <div 
            className="music-sheets-modal-overlay" 
            onClick={() => setShowGenerateModal(false)}
          />
          <div className="music-sheets-modal">
            <div className="music-sheets-modal-header">
              <h2 className="music-sheets-modal-title">Generate Music Sheet</h2>
              <button 
                className="music-sheets-modal-close" 
                onClick={() => setShowGenerateModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="music-sheets-modal-content">
              {/* Text Prompt */}
              <div className="music-sheets-form-group">
                <label htmlFor="text-prompt" className="music-sheets-label">
                  Description (Optional)
                </label>
                <textarea
                  id="text-prompt"
                  className="music-sheets-textarea"
                  placeholder="e.g., A cheerful waltz in the style of Strauss, or A mysterious night-time melody..."
                  value={textPrompt}
                  onChange={(e) => setTextPrompt(e.target.value)}
                  rows={3}
                />
              </div>

              <div className="music-sheets-divider">
                <span>OR use structured inputs</span>
              </div>

              {/* Structured Inputs */}
              <div className="music-sheets-form-row">
                <div className="music-sheets-form-group">
                  <label htmlFor="genre" className="music-sheets-label">Genre</label>
                  <select
                    id="genre"
                    className="music-sheets-select"
                    value={genre}
                    onChange={(e) => setGenre(e.target.value)}
                  >
                    <option value="">Select genre...</option>
                    <option value="classical">Classical</option>
                    <option value="folk">Folk</option>
                    <option value="waltz">Waltz</option>
                    <option value="march">March</option>
                    <option value="lullaby">Lullaby</option>
                    <option value="dance">Dance</option>
                    <option value="jazz">Jazz</option>
                  </select>
                </div>

                <div className="music-sheets-form-group">
                  <label htmlFor="tempo" className="music-sheets-label">Tempo (BPM)</label>
                  <input
                    id="tempo"
                    type="text"
                    className="music-sheets-input"
                    placeholder="e.g., 120"
                    value={tempo}
                    onChange={(e) => setTempo(e.target.value)}
                  />
                </div>
              </div>

              <div className="music-sheets-form-row">
                <div className="music-sheets-form-group">
                  <label htmlFor="key" className="music-sheets-label">Key</label>
                  <select
                    id="key"
                    className="music-sheets-select"
                    value={key}
                    onChange={(e) => setKey(e.target.value)}
                  >
                    <option value="">Select key...</option>
                    <option value="C">C Major</option>
                    <option value="G">G Major</option>
                    <option value="D">D Major</option>
                    <option value="A">A Major</option>
                    <option value="E">E Major</option>
                    <option value="F">F Major</option>
                    <option value="Am">A Minor</option>
                    <option value="Em">E Minor</option>
                    <option value="Dm">D Minor</option>
                  </select>
                </div>

                <div className="music-sheets-form-group">
                  <label htmlFor="mood" className="music-sheets-label">Mood</label>
                  <input
                    id="mood"
                    type="text"
                    className="music-sheets-input"
                    placeholder="e.g., cheerful, calm, energetic"
                    value={mood}
                    onChange={(e) => setMood(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="music-sheets-modal-footer">
              <button 
                className="music-sheets-btn-cancel"
                onClick={() => setShowGenerateModal(false)}
              >
                Cancel
              </button>
              <button 
                className="music-sheets-btn-generate"
                onClick={handleGenerate}
              >
                Generate Music
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MusicSheets;

